import {RefObject, useCallback, useEffect, useId, useRef} from "react";
import {useLocalStorageState} from "@bounce/shared/hooks";

export const FILTER_PANEL_SHORTCUT = "[";

export type FilterPanelState = {
  isExpanded: boolean;
  toggle: () => void;
  panelId: string;
  collapseButtonRef: RefObject<HTMLButtonElement>;
  showButtonWrapperRef: RefObject<HTMLSpanElement>;
};

const isTypingTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));

export const useFilterPanel = (storageKey: string): FilterPanelState => {
  const [isExpanded, setIsExpanded] = useLocalStorageState<boolean>(storageKey, true, {encrypted: false});
  const panelId = useId();
  const collapseButtonRef = useRef<HTMLButtonElement>(null);
  const showButtonWrapperRef = useRef<HTMLSpanElement>(null);
  const shouldMoveFocusRef = useRef(false);

  const toggle = useCallback(() => {
    shouldMoveFocusRef.current = true;
    setIsExpanded((prev) => !prev);
  }, [setIsExpanded]);

  useEffect(() => {
    if (!shouldMoveFocusRef.current) return;
    shouldMoveFocusRef.current = false;
    if (isExpanded) {
      collapseButtonRef.current?.focus();
    } else {
      showButtonWrapperRef.current?.querySelector("button")?.focus();
    }
  }, [isExpanded]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== FILTER_PANEL_SHORTCUT || event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;
      event.preventDefault();
      toggle();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [toggle]);

  return {isExpanded, toggle, panelId, collapseButtonRef, showButtonWrapperRef};
};
